/**
 * DateTime Picker Interface
 * Proper datetime picker using shadcn components
 */

import { useState, useMemo } from 'react';
import { format, parse, parseISO, isValid } from 'date-fns';
import { Calendar as CalendarIcon, Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

interface DateTimePickerProps {
    value: string | null;
    onChange: (value: string | null) => void;
    type?: 'date' | 'datetime' | 'time';
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    displayFormat?: string;
}

export function DateTimePicker({
    value,
    onChange,
    type = 'datetime',
    disabled = false,
    placeholder,
    className,
    displayFormat,
}: DateTimePickerProps) {
    const [open, setOpen] = useState(false);
    const [timeValue, setTimeValue] = useState('');

    // Parse the value into a Date object
    const dateValue = useMemo(() => {
        if (!value) return undefined;

        try {
            // Handle ISO strings and common formats
            let date: Date;

            if (value.includes('T')) {
                // ISO format
                date = parseISO(value);
            } else if (type === 'date') {
                // Date only: YYYY-MM-DD
                date = parse(value, 'yyyy-MM-dd', new Date());
            } else if (type === 'time') {
                // Time only: HH:mm or HH:mm:ss
                const today = new Date();
                const [hours, minutes] = value.split(':').map(Number);
                today.setHours(hours || 0, minutes || 0, 0, 0);
                date = today;
            } else {
                // Try parsing as ISO first
                date = parseISO(value);
                if (!isValid(date)) {
                    // Fallback to datetime-local format
                    date = parse(value, "yyyy-MM-dd'T'HH:mm", new Date());
                }
            }

            return isValid(date) ? date : undefined;
        } catch {
            return undefined;
        }
    }, [value, type]);

    // Extract time string for time input
    const currentTime = useMemo(() => {
        if (dateValue) {
            return format(dateValue, 'HH:mm');
        }
        return timeValue || '';
    }, [dateValue, timeValue]);

    // Format value for display
    const displayValue = useMemo(() => {
        if (!dateValue) return '';

        if (displayFormat) {
            try {
                return format(dateValue, displayFormat);
            } catch {
                // fallback to defaults if format string is invalid
            }
        }

        switch (type) {
            case 'date':
                return format(dateValue, 'PPP');
            case 'time':
                return format(dateValue, 'p');
            case 'datetime':
            default:
                return format(dateValue, 'PPP p');
        }
    }, [dateValue, type, displayFormat]);

    // Handle date selection from calendar
    const handleDateSelect = (date: Date | undefined) => {
        if (!date) {
            onChange(null);
            return;
        }

        let newDate = date;

        // Preserve time if editing datetime
        if (type === 'datetime' && dateValue) {
            newDate.setHours(dateValue.getHours(), dateValue.getMinutes());
        } else if (type === 'datetime' && currentTime) {
            const [hours, minutes] = currentTime.split(':').map(Number);
            newDate.setHours(hours || 0, minutes || 0);
        }

        emitValue(newDate);

        if (type === 'date') {
            setOpen(false);
        }
    };

    // Handle time change
    const handleTimeChange = (time: string) => {
        setTimeValue(time);

        if (!time) {
            if (type === 'time') {
                onChange(null);
            }
            return;
        }

        const [hours, minutes] = time.split(':').map(Number);

        if (type === 'time') {
            onChange(time);
            return;
        }

        // For datetime, update the date with new time
        const newDate = dateValue ? new Date(dateValue) : new Date();
        newDate.setHours(hours || 0, minutes || 0, 0, 0);
        emitValue(newDate);
    };

    // Emit the formatted value
    const emitValue = (date: Date) => {
        switch (type) {
            case 'date':
                onChange(format(date, 'yyyy-MM-dd'));
                break;
            case 'time':
                onChange(format(date, 'HH:mm:ss'));
                break;
            case 'datetime':
            default:
                onChange(date.toISOString());
                break;
        }
    };

    // Handle clear
    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
        setTimeValue('');
    };

    // Time-only picker
    if (type === 'time') {
        return (
            <div className={cn('flex gap-2', className)}>
                <div className="relative flex-1">
                    <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        type="time"
                        value={currentTime}
                        onChange={(e) => handleTimeChange(e.target.value)}
                        disabled={disabled}
                        className="pl-10"
                        placeholder={placeholder || 'Select time'}
                    />
                </div>
                {value && !disabled && (
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleClear}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                        'w-full justify-start text-left font-normal',
                        !displayValue && 'text-muted-foreground',
                        className
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {displayValue || (placeholder || (type === 'date' ? 'Pick a date' : 'Pick date and time'))}
                    {value && !disabled && (
                        <X
                            className="ml-auto h-4 w-4 hover:text-destructive"
                            onClick={handleClear}
                        />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={dateValue}
                    onSelect={handleDateSelect}
                    initialFocus
                />
                {type === 'datetime' && (
                    <div className="border-t p-3">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <Input
                                type="time"
                                value={currentTime}
                                onChange={(e) => handleTimeChange(e.target.value)}
                                className="flex-1"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setOpen(false)}
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
